
import React, { useState } from "react";
import { Editor } from "@tiptap/react";
import EditorButtonGroup from "../buttons/EditorButtonGroup";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Paintbrush, PaintBucket } from "lucide-react";

interface ColorButtonsProps {
  editor: Editor;
}

const ColorButtons = ({ editor }: ColorButtonsProps) => {
  const [isTextColorOpen, setIsTextColorOpen] = useState(false);
  const [isBackgroundColorOpen, setIsBackgroundColorOpen] = useState(false);

  const colors = [
    "#000000", // black
    "#ffffff", // white
    "#ff0000", // red
    "#00ff00", // green
    "#0000ff", // blue
    "#ffff00", // yellow
    "#ff00ff", // magenta
    "#00ffff", // cyan
    "#ffa500", // orange
    "#800080", // purple
    "#808080", // gray
    "#a52a2a", // brown
  ];

  if (!editor) {
    return null;
  }

  const setBackgroundColor = (color: string) => {
    editor.chain().focus().setMark('textStyle', { backgroundColor: color }).run();
  };

  const unsetBackgroundColor = () => {
    editor.chain().focus().setMark('textStyle', { backgroundColor: null }).run();
  };

  return (
    <EditorButtonGroup>
      <Popover open={isTextColorOpen} onOpenChange={setIsTextColorOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-gray-100"
            title="Text Color"
          >
            <Paintbrush className="h-4 w-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-2">
          <div className="grid grid-cols-6 gap-1">
            {colors.map((color) => (
              <button
                key={color}
                type="button"
                className="h-6 w-6 rounded-full border border-gray-200"
                style={{ backgroundColor: color }}
                onClick={() => {
                  editor.chain().focus().setColor(color).run();
                  setIsTextColorOpen(false);
                }}
              />
            ))}
            <button
              type="button"
              className="h-6 w-6 rounded-full border border-gray-200 flex items-center justify-center text-xs"
              onClick={() => {
                editor.chain().focus().unsetColor().run();
                setIsTextColorOpen(false);
              }}
            >
              X
            </button>
          </div>
        </PopoverContent>
      </Popover>

      <Popover open={isBackgroundColorOpen} onOpenChange={setIsBackgroundColorOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-gray-100"
            title="Background Color"
          >
            <PaintBucket className="h-4 w-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-2">
          <div className="grid grid-cols-6 gap-1">
            {colors.map((color) => (
              <button
                key={color}
                type="button"
                className="h-6 w-6 rounded-full border border-gray-200"
                style={{ backgroundColor: color }}
                onClick={() => {
                  setBackgroundColor(color);
                  setIsBackgroundColorOpen(false);
                }}
              />
            ))}
            <button
              type="button"
              className="h-6 w-6 rounded-full border border-gray-200 flex items-center justify-center text-xs"
              onClick={() => {
                unsetBackgroundColor();
                setIsBackgroundColorOpen(false);
              }}
            >
              X
            </button>
          </div>
        </PopoverContent>
      </Popover>
    </EditorButtonGroup>
  );
};

export default ColorButtons;
